import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, CircleHelp, ExternalLink } from 'lucide-react';
import { ProgressIndicator } from '../ui/flow-progress-indicator';
import { Button } from '../ui/button';
import { useAuth } from '../../contexts/AuthContext';
import { INTAKE_ASSESSMENT_FLOW_NAME } from 'constants/flows';
import { getTestId } from 'utils/testUtils';
import { useFlowCourseContext } from '../QuestionFlow/useFlowCourseName';
import {
  FlowThemeHeaderScrim,
  FlowThemeSurfaceProvider,
  resolveFlowTheme,
  type FlowThemeProgress,
} from '../QuestionFlow/themes';

interface QuestionLayoutProps {
  children: React.ReactNode;
  progress: number;
  flowName?: string;
  flowIdentifier?: string;
  courseId?: number | null;
  /** Canonical course key supplied by the authorized module response. */
  courseContentKey?: string | null;
  onOpenTutorial?: () => void;
  headerActions?: React.ReactNode;
  backAction?: { label: string; onClick: () => void };
  /** The flow's background theme, which restyles the header and cards once questions show. */
  theme?: string | null;
  themeProgress?: FlowThemeProgress | null;
  /**
   * Whether the theme's backdrop is showing yet. The header keeps its default
   * colours until then, so white text never sits on the light default art
   * while the backdrop is still loading or fading in.
   */
  themeReady?: boolean;
}

export const QuestionLayout: React.FC<QuestionLayoutProps> = ({
  children,
  progress,
  flowName,
  flowIdentifier,
  courseId,
  courseContentKey,
  onOpenTutorial,
  headerActions,
  backAction,
  theme,
  themeProgress,
  themeReady = false,
}) => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const courseContext = useFlowCourseContext(flowIdentifier, courseId);
  const courseName = courseContext?.name;
  const resolvedCourseId = courseContext?.resolvedId ?? courseId;
  const showSnapshotLink = courseContentKey === 'life-plan' && Boolean(resolvedCourseId);
  const backLabel = backAction?.label ?? (resolvedCourseId ? 'Back to course' : 'Dashboard');
  const themeDefinition = resolveFlowTheme(theme);
  const isThemed = Boolean(themeProgress);
  const chromeTone = isThemed && themeReady && themeProgress
    ? themeDefinition.chromeTone(themeProgress.step / Math.max(themeProgress.total, 1))
    : undefined;

  const handleSignOut = useCallback(() => {
    logout();
    navigate('/login');
  }, [logout, navigate]);

  return (
    <div
      className="flow-theme-scope"
      data-testid="question-layout"
      data-flow-theme={isThemed ? themeDefinition.id : undefined}
      data-flow-card-glass={isThemed ? themeDefinition.cardGlass : undefined}
      data-flow-chrome={chromeTone}
    >
      {isThemed ? <FlowThemeHeaderScrim tone={chromeTone} /> : null}
      <header className="top-0 left-0 right-0 z-50 w-full bg-transparent">
        {(flowIdentifier ?? flowName) === INTAKE_ASSESSMENT_FLOW_NAME ? (
          <div
            className="grid w-full grid-cols-[minmax(0,1fr)_auto] items-center gap-2 px-3 pt-2 sm:px-6 sm:pt-3 md:grid-cols-[1fr_auto_1fr] md:px-10 md:pt-6"
            {...getTestId('intake-question-header')}
          >
            <div className="hidden md:flex md:flex-1" />
            <div className="min-w-0 md:col-start-2 md:flex md:justify-center">
              <ProgressIndicator progress={progress} flowName={flowName}/>
            </div>
            <div className="flex items-center justify-self-end gap-2 md:col-start-3">
              {headerActions ? <div className="shrink-0">{headerActions}</div> : null}
              <Button
                size='auto'
                onClick={handleSignOut}
                className="px-4 py-2 font-semibold md:px-5 md:py-3"
              >
                Sign out
              </Button>
            </div>
          </div>
        ) : (
          <div className={`flex w-full items-center justify-between gap-3 px-4 pt-3 sm:px-6 md:px-10 ${showSnapshotLink ? 'flex-wrap gap-y-0 sm:flex-nowrap' : ''} ${courseName ? 'sm:pt-4' : 'sm:pt-6'}`}>
            <div className="flex shrink-0 items-center gap-3">
              <button
                className="inline-flex items-center gap-2 transition-opacity hover:opacity-80"
                onClick={backAction?.onClick ?? (() => navigate(resolvedCourseId ? `/training?courseId=${resolvedCourseId}#courses` : '/home'))}
                aria-label={backLabel}
              >
                <ArrowLeftIcon className="flow-chrome-text w-5 h-5 text-foundation-base-darkbase-dark-500" />
                <span className="flow-chrome-text hidden type-body-m-semibold text-foundation-base-darkbase-dark-500 sm:inline">
                  {backLabel}
                </span>
              </button>
            </div>

            <div className="flex min-w-0 flex-1 items-center justify-end gap-2 pl-1 md:w-auto md:pl-4">
              {headerActions ? (
                <div className="shrink-0">
                  {headerActions}
                </div>
              ) : null}
              <div className="min-w-0 flex-1 md:flex-none">
                <ProgressIndicator progress={progress} flowName={flowName} courseName={courseName} chapterName={courseContext?.chapterTitle} lessonPosition={courseContext?.lessonPosition} lessonCount={courseContext?.lessonCount}/>
              </div>
              {onOpenTutorial ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="flow-chrome-muted h-8 w-8 shrink-0 rounded-full text-foundation-base-darkbase-dark-500/45 hover:bg-transparent hover:text-foundation-base-darkbase-dark-500/70"
                  aria-label="Open tutorial"
                  onClick={onOpenTutorial}
                  {...getTestId('question-flow-tutorial-button')}
                >
                  <CircleHelp className="h-4 w-4" />
                </Button>
              ) : null}
            </div>
            {showSnapshotLink ? (
              <a
                href="/snapshot"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="View Snapshot (opens in a new tab)"
                title="View Snapshot (opens in a new tab)"
                className="flow-chrome-text ml-auto inline-flex min-h-10 shrink-0 basis-full items-center justify-end gap-1.5 text-sm font-semibold text-foundation-base-darkbase-dark-500 underline underline-offset-4 transition-opacity hover:opacity-80 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 sm:min-w-10 sm:basis-auto sm:justify-center"
                {...getTestId('question-flow-snapshot-link')}
              >
                <span className="sm:hidden md:inline">View Snapshot</span>
                <ExternalLink className="h-4 w-4" aria-hidden="true" />
              </a>
            ) : null}
          </div>
        )}
      </header>

      <FlowThemeSurfaceProvider themed={isThemed}>
        {children}
      </FlowThemeSurfaceProvider>
    </div>
  );
};
